import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FONT, CONVERT_OPTIONS } from "../logic/chartConfig"
import { chartColorSetting, changeChartSetting } from "redux/ducks/Chart"
import chartColors from "../logic/chartColor"
import ChartInputNumeric from "./ChartInputNumeric"
import ChartColorPicker from "./ChartColorPicker"
import ChartBaseSelector from "./ChartBaseSelector"
import _ from "lodash"

interface props {
  canvasId: string
  displayTitle: string
  targetGroup: string
  target: string
}

interface baseProps extends props {
  chartType: string
  param?: Array<string>
}

export const ChartFontSelector = React.memo(
  (props: {
    chartType: string
    title?: string
    targetGroup: string
    target: string
    canvasId: string
  }) => {
    const { targetGroup, target } = props

    const dispatch = useDispatch()
    const plotTypeSelector = (state: any) =>
      state.chart[props.canvasId].plotType
    const providerSelector = (state: any) =>
      state.chart[props.canvasId].providers
    const colorSettingSelector = (state: any) => state.chart.colorSetting

    const plotType = useSelector(plotTypeSelector)
    const providers = useSelector(providerSelector)
    const colorSetting = useSelector(colorSettingSelector)
    const [_provider, _controller, _procs] = providers
    const [selectedFont, setSelectedFont] = useState(
      (() => {
        // reduxのcolorSettingからdefault値を取得。ここでしか使用しないため即時関数で実行。
        const colorType = props.chartType
        if (!CONVERT_OPTIONS[target]) return
        let path = _.join(
          [colorType, targetGroup, CONVERT_OPTIONS[target][0]],
          "."
        )
        let vals = _.get(colorSetting, path).split(";")
        return vals[1]
      })()
    )

    const setFont = useCallback((id: number) => {
      setSelectedFont(FONT[id].key)
    }, [])

    const _setFont = () => {
      const colorType = props.chartType
      let newSettings = {}
      const { targetGroup, target } = props
      if (!CONVERT_OPTIONS[target]) return

      let path = _.join(
        [colorType, targetGroup, CONVERT_OPTIONS[target][0]],
        "."
      )
      let vals = _.get(colorSetting, path).split(";")
      if (vals && vals.length > 0) {
        newSettings = _.merge(
          {},
          colorSetting,
          _.set({}, path, vals[0] + ";" + selectedFont)
        )
      } else {
        newSettings = _.merge({}, colorSetting)
      }
      // 線が2本ある場合、2本目もサイズを変更する。Reduxで管理しているためforEachで回しても更新が追い付かない。
      if (CONVERT_OPTIONS[target].length === 2) {
        let path2 = _.join(
          [colorType, targetGroup, CONVERT_OPTIONS[target][1]],
          "."
        )
        let vals2 = _.get(colorSetting, path2).split(";")
        if (vals2 && vals2.length > 0) {
          newSettings = _.merge(
            {},
            newSettings,
            _.set({}, path2, vals2[0] + ";" + selectedFont)
          )
        } else {
          newSettings = _.merge({}, newSettings)
        }
      }

      dispatch(chartColorSetting(newSettings))
    }

    useEffect(() => {
      _setFont()
    }, [selectedFont])

    useEffect(() => {
      chartColors.setColorPattern(plotType, colorSetting, _controller)
    }, [colorSetting])

    return (
      <div className="chart-font-selector">
        <ChartBaseSelector
          list={FONT}
          onChange={setFont}
          defaltValueId={(() => {
            if (selectedFont === FONT[0].key) return FONT[0].id
            if (selectedFont === FONT[1].key) return FONT[1].id
            if (selectedFont === FONT[2].key) return FONT[2].id
            return 0
          })()}
        />
      </div>
    )
  }
)

export const AddonAndSubPropetyBase = React.memo((props: baseProps) => {
  const {
    chartType,
    canvasId,
    displayTitle,
    targetGroup,
    target,
    param,
  } = props

  const dispatch = useDispatch()
  const chartSettingSelector = (state: any) => state.chart.chartSetting
  const chartSetting = useSelector(chartSettingSelector)

  const _changeChartSetting = useCallback((value: number) => {
    const _chartSetting = _.assign({}, chartSetting)
    _.set(_chartSetting, param, value)
    dispatch(changeChartSetting(_chartSetting))
  }, [])

  return (
    <div className="chart-setting">
      <ChartInputNumeric
        mini={true}
        defaultValue={_.get(chartSetting, param)}
        min={_.get(chartSetting, [
          chartType,
          targetGroup,
          "_Def",
          target,
          "min",
        ])}
        max={_.get(chartSetting, [
          chartType,
          targetGroup,
          "_Def",
          target,
          "max",
        ])}
        step={_.get(chartSetting, [
          chartType,
          targetGroup,
          "_Def",
          target,
          "step",
        ])}
        decimalDigits={_.get(chartSetting, [
          chartType,
          targetGroup,
          "_Def",
          target,
          "precision",
        ])}
        setValue={(value: number) => {
          _changeChartSetting(value)
        }}
      />
      <ChartFontSelector
        chartType={chartType}
        title={displayTitle}
        targetGroup={targetGroup}
        target={target}
        canvasId={canvasId}
      />
      <ChartColorPicker
        type={chartType}
        targetGroup={targetGroup}
        target={target}
        parentId={"tech-setting"}
      />
    </div>
  )
})

const VIEW_TYPE_1 = [
  "Parabolic_AF",
  "Parabolic_Step",
  "Parabolic_Max",
  "ATRChannel_Atr",
  "HLBand_CenterLine",
  "DelayLine",
  "Span_Value",
  "Parabolic_Up",
  "Parabolic_Down",
  "PrecedLine1",
  "PrecedLine2",
  "Kumo",
  "Kumo2",
  "VWAP",
]

const VIEW_TYPE_2 = [
  "Parabolic_AF",
  "Parabolic_Step",
  "Parabolic_Max",
  "ATRChannel_Atr",
  "Span_Value",
]

const VIEW_TYPE_3 = ["HLBand_CenterLine", "DelayLine", "VWAP"]

const VIEW_TYPE_4 = [
  "Parabolic_Up",
  "Parabolic_Down",
  "PrecedLine1",
  "PrecedLine2",
  "Kumo",
  "Kumo2",
]

const ChartAddonPropety = React.memo((props: props) => {
  const { canvasId, displayTitle, targetGroup, target } = props
  const dispatch = useDispatch()
  const termSelector = (state: any) => state.chart[canvasId].term
  const chartSettingSelector = (state: any) => state.chart.chartSetting
  const term = useSelector(termSelector)
  const chartSetting = useSelector(chartSettingSelector)
  const _param = useMemo(() => {
    return !_.get(
      chartSetting,
      ["addon", targetGroup, "_Def", target, term.key],
      null
    )
      ? ["addon", targetGroup, "_Def", target, "default"]
      : ["addon", targetGroup, "_Def", target, term.key]
  }, [term])

  const _changeChartSetting = useCallback((value: number) => {
    const _chartSetting = _.clone(chartSetting)
    _.set(_chartSetting, _param, value)
    dispatch(changeChartSetting(_chartSetting))
  }, [])

  return (
    <>
      {!_.includes(VIEW_TYPE_1, target) && (
        <AddonAndSubPropetyBase
          chartType={"addon"}
          displayTitle={displayTitle}
          targetGroup={targetGroup}
          target={target}
          canvasId={canvasId}
          param={_param}
        />
      )}
      {_.includes(VIEW_TYPE_2, target) && (
        <ChartInputNumeric
          mini={true}
          defaultValue={_.get(chartSetting, _param)}
          min={_.get(chartSetting, [
            "addon",
            targetGroup,
            "_Def",
            target,
            "min",
          ])}
          max={_.get(chartSetting, [
            "addon",
            targetGroup,
            "_Def",
            target,
            "max",
          ])}
          step={_.get(chartSetting, [
            "addon",
            targetGroup,
            "_Def",
            target,
            "step",
          ])}
          decimalDigits={_.get(chartSetting, [
            "addon",
            targetGroup,
            "_Def",
            target,
            "precision",
          ])}
          setValue={(value: number) => {
            _changeChartSetting(value)
          }}
        />
      )}
      {_.includes(VIEW_TYPE_3, target) && (
        <div className="chart-setting">
          <ChartFontSelector
            chartType={"addon"}
            title={displayTitle}
            targetGroup={targetGroup}
            target={target}
            canvasId={canvasId}
          />
          <ChartColorPicker
            type={"addon"}
            targetGroup={targetGroup}
            target={target}
            parentId={"tech-setting"}
          />
        </div>
      )}
      {_.includes(VIEW_TYPE_4, target) && (
        <ChartColorPicker
          type={"addon"}
          targetGroup={targetGroup}
          target={target}
          parentId={"tech-setting"}
        />
      )}
    </>
  )
})

export default ChartAddonPropety
